import api from './http';

const CONSENT_KEY = 'rassplAnalyticsConsent';
const VISITOR_COOKIE = 'rassplVisitorId';
const CONSENT_VERSION = '2026-10-09';
const CONSENT_DETAILS_KEY = 'rassplAnalyticsConsentDetails';
const CONSENT_LIFETIME_MS = 180 * 24 * 60 * 60 * 1000;
const PUBLIC_PATHS = new Set([
  '/',
  '/about',
  '/rules',
  '/competitions',
  '/document-fish',
  '/fish-calculator',
  '/sponsors',
  '/sponsors/',
  '/contact',
  '/login',
  '/signup',
]);

export function hasAnalyticsConsent() {
  return hasStoredAnalyticsChoice() && localStorage.getItem(CONSENT_KEY) === 'accepted';
}

export function hasStoredAnalyticsChoice() {
  try {
    const details = JSON.parse(localStorage.getItem(CONSENT_DETAILS_KEY) || 'null');
    const age = Date.now() - Date.parse(details?.recordedAt);
    if (['accepted', 'rejected'].includes(localStorage.getItem(CONSENT_KEY))
        && details?.version === CONSENT_VERSION && age >= 0 && age < CONSENT_LIFETIME_MS) {
      return true;
    }
  } catch {
    // An unreadable preference is not permission to track.
  }
  localStorage.removeItem(CONSENT_KEY);
  localStorage.removeItem(CONSENT_DETAILS_KEY);
  clearCookie(VISITOR_COOKIE);
  return false;
}

function storeChoice(choice) {
  localStorage.setItem(CONSENT_KEY, choice);
  localStorage.setItem(CONSENT_DETAILS_KEY, JSON.stringify({
    version: CONSENT_VERSION,
    recordedAt: new Date().toISOString(),
  }));
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event('rasspl-open-cookie-preferences'));
}

export function acceptAnalytics(route) {
  storeChoice('accepted');
  ensureVisitorId();
  window.dispatchEvent(new Event('rasspl-analytics-consent-changed'));
  trackPageView(route);
}

export function rejectAnalytics() {
  storeChoice('rejected');
  clearCookie(VISITOR_COOKIE);
  window.dispatchEvent(new Event('rasspl-analytics-consent-changed'));
}

export function trackPageView(route) {
  if (!hasAnalyticsConsent() || !route || !isPublicPath(route.path)) {
    return;
  }

  const payload = {
    visitorId: ensureVisitorId(),
    consentVersion: CONSENT_VERSION,
    path: route.path,
    pageTitle: document.title,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent || null,
  };

  const url = `${api.defaults.baseURL || ''}rasspl/api/analytics/page-view`;

  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) {
      return;
    }
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

export function isPublicPath(path) {
  return PUBLIC_PATHS.has(path);
}

function ensureVisitorId() {
  const existingVisitorId = getCookie(VISITOR_COOKIE);
  if (existingVisitorId) {
    return existingVisitorId;
  }

  const visitorId = createVisitorId();
  setCookie(VISITOR_COOKIE, visitorId, 365);
  return visitorId;
}

function createVisitorId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return 'visitor-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getCookie(name) {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=');
}

function setCookie(name, value, days) {
  const maxAge = days * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; Max-Age=${maxAge}; path=/; SameSite=Lax${secure}`;
}

function clearCookie(name) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${secure}`;
}
